/**
 * Follett Access sales eligibility (APDP / Quick View).
 * If any product on the PDP (root or unified sibling) has IncludEDMaterialFlag true
 * in section_data_mapping, the whole PDP is Access: that flag is assumed for all
 * digital variants. Digitals without "SC" in sales_channel are hidden.
 * Variants whose SKU is in shop.metafields.custom.noPurchase_AccessSKUs (when set)
 * are hidden on Access PDPs regardless of format or sales channel.
 * If a print tab remains, ATB stays visible. Hide ATB only when there is no print
 * tab and Access has hidden every digital option.
 */
(function () {
  'use strict';

  /**
   * Canonical display for campus/API "Follett Access" (any casing). Not a settings fallback.
   */
  const DEFAULT_PROGRAM_NAME = 'Follett Access';
  const ACCESS_READY_TIMEOUT_MS = 20000;

  /**
   * Normalize campus Included Program Name for display.
   * "Follett Access" (any casing) → "Follett Access"; other names pass through.
   * @param {unknown} name
   * @returns {string}
   */
  function formatAccessProgramName(name) {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (!trimmed) return '';
    if (/^follett\s+access$/i.test(trimmed)) return DEFAULT_PROGRAM_NAME;
    return trimmed;
  }

  if (!window.__pdpFollettAccessReadyPromise) {
    window.__pdpFollettAccessReadyPromise = new Promise((resolve) => {
      window.__resolvePdpFollettAccessReady = () => {
        resolve();
        window.__resolvePdpFollettAccessReady = null;
      };
    });
  }

  /**
   * Signal that PDP variant/access UI is ready (unified merge, access filter, tabs revealed).
   */
  function markPdpFollettAccessReady() {
    if (typeof window.__resolvePdpFollettAccessReady === 'function') {
      window.__resolvePdpFollettAccessReady();
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async function waitForPdpFollettAccessReady() {
    const needsWait =
      !!document.querySelector('.textbook-variant-picker, .format-tabs-wrapper') ||
      !!document.querySelector('[data-fa-pdp-pending]');
    if (!needsWait || !window.__pdpFollettAccessReadyPromise) return;

    await Promise.race([
      window.__pdpFollettAccessReadyPromise,
      new Promise((resolve) => setTimeout(resolve, ACCESS_READY_TIMEOUT_MS)),
    ]);
  }

  /**
   * Theme setting from window.shopifySettings (schema / Liquid defaults).
   * @param {string} key
   * @returns {string}
   */
  function getAccessProgramSetting(key) {
    const g = window.shopifySettings;
    const value = g && typeof g[key] === 'string' ? g[key].trim() : '';
    return value;
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function escapeAccessProgramHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {unknown} name
   * @returns {string}
   */
  function resolveAccessProgramLabel(name) {
    const formatted = formatAccessProgramName(name);
    if (formatted) return formatted;
    return getAccessProgramSetting('cmrpAccessProgramDefaultName') || DEFAULT_PROGRAM_NAME;
  }

  /**
   * @param {string} template
   * @param {unknown} label
   * @param {boolean} [wrapTitle]
   * @returns {string}
   */
  function fillAccessProgramPlaceholderForHtml(template, label, wrapTitle = false) {
    const resolved = escapeAccessProgramHtml(resolveAccessProgramLabel(label));
    const replacement = wrapTitle
      ? `<span class="access-program-title">${resolved}</span>`
      : resolved;
    return escapeAccessProgramHtml(String(template ?? '')).replace(
      /\[Access Program\]/g,
      replacement
    );
  }

  /**
   * @param {unknown} raw
   * @returns {boolean}
   */
  function salesChannelTokensContainSc(raw) {
    return String(raw ?? '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
      .includes('SC');
  }

  /**
   * Shop metafield custom.noPurchase_AccessSKUs (window.shopifySettings.noPurchaseAccessSKUs).
   * Null when the metafield is missing or empty.
   * @returns {Set<string> | null}
   */
  function getNoPurchaseAccessSkuSet() {
    const raw =
      typeof window !== 'undefined' ? window.shopifySettings?.noPurchaseAccessSKUs : null;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const set = new Set(raw.map((s) => String(s ?? '').trim()).filter(Boolean));
    return set.size > 0 ? set : null;
  }

  /**
   * @param {unknown} sku
   * @returns {boolean}
   */
  function isNoPurchaseAccessSku(sku) {
    const set = getNoPurchaseAccessSkuSet();
    if (!set) return false;
    const needle = String(sku ?? '').trim();
    return needle !== '' && set.has(needle);
  }

  /**
   * @param {unknown} variant
   * @returns {string}
   */
  function getVariantSalesChannelRaw(variant) {
    const mf = variant?.metafields;
    if (!mf) return '';

    if (typeof mf === 'object' && !Array.isArray(mf)) {
      if (mf.cm_digital?.sales_channel != null) {
        return String(mf.cm_digital.sales_channel);
      }
      if (mf.custom?.sales_channel != null) {
        return String(mf.custom.sales_channel);
      }
    }

    if (Array.isArray(mf)) {
      const cmDigitalRow = mf.find(
        (x) =>
          x &&
          x.namespace === 'cm_digital' &&
          (x.key === 'sales_channel' || x.sales_channel != null),
      );
      if (cmDigitalRow?.sales_channel != null) {
        return String(cmDigitalRow.sales_channel);
      }
      const customRow = mf.find(
        (x) =>
          x &&
          x.namespace === 'custom' &&
          (x.key === 'sales_channel' || x.sales_channel != null),
      );
      if (customRow?.sales_channel != null) {
        return String(customRow.sales_channel);
      }
    }

    return '';
  }

  /**
   * @param {unknown} variant
   * @param {boolean} [includedMaterial]
   * @returns {boolean}
   */
  function isDigitalVariant(variant, includedMaterial = false) {
    const mf = variant?.metafields;
    if (mf) {
      if (Array.isArray(mf)) {
        const hit = mf.find((m) => m && m.is_digital != null);
        if (hit) {
          const v = hit.is_digital;
          if (v === true || String(v).toLowerCase() === 'true') return true;
        }
      } else if (typeof mf === 'object') {
        const v = mf.cm_digital?.is_digital ?? mf.is_digital;
        if (v != null) {
          return v === true || String(v).toLowerCase() === 'true';
        }
      }
    }
    const opt1 = String(variant?.option1 ?? '').toLowerCase();
    if (opt1.includes('digital')) return true;
    if (includedMaterial && String(variant?.option2 ?? '').trim() === '') return true;
    return false;
  }

  /**
   * @param {unknown} mapping
   * @returns {boolean}
   */
  function productHasIncludedMaterialFlag(mapping) {
    let arr = mapping;
    if (arr == null) return false;
    if (typeof arr === 'string') {
      try {
        arr = JSON.parse(arr);
      } catch {
        return false;
      }
    }
    if (!Array.isArray(arr) || arr.length === 0) return false;
    return arr.some((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const flag =
        entry.IncludEDMaterialFlag ??
        entry.includedMaterialFlag ??
        entry.IncludedMaterialFlag;
      return flag != null && String(flag).toLowerCase() === 'true';
    });
  }

  /**
   * @param {unknown} metafields
   * @param {string} namespace
   * @param {string} key
   * @returns {unknown}
   */
  function getMetafieldFromArray(metafields, namespace, key) {
    if (!Array.isArray(metafields)) return null;
    for (const row of metafields) {
      if (!row || typeof row !== 'object') continue;
      if (row.namespace !== namespace) continue;
      if (row[key] != null) return row[key];
      if (row.key === key && row.value != null) return row.value;
    }
    return null;
  }

  /**
   * @param {unknown} raw
   * @returns {unknown}
   */
  function normalizeSectionDataMapping(raw) {
    if (raw == null || raw === '') return [];
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }
    // Metafield wrapper: { value: "..." | [...] }
    if (typeof raw === 'object' && !Array.isArray(raw) && raw.value != null) {
      return normalizeSectionDataMapping(raw.value);
    }
    return raw;
  }

  /**
   * Read section_data_mapping from product shapes returned by Liquid, Admin-style
   * metafield arrays, nested course_materials, or getDigitalProducts flat payload:
   * `{ metafields: { section_data_mapping: "<json>" } }`.
   * @param {unknown} product
   * @returns {unknown}
   */
  function extractSectionDataMappingFromProduct(product) {
    if (!product || typeof product !== 'object') return null;
    const direct =
      product.sectionDataMapping ?? product.section_data_mapping ?? null;
    if (direct != null && direct !== '') return direct;

    const mf = product.metafields;
    if (!mf) return null;
    if (Array.isArray(mf)) {
      return getMetafieldFromArray(mf, 'course_materials', 'section_data_mapping');
    }
    if (typeof mf !== 'object') return null;

    // Nested (Liquid / CMRP products endpoint) and flat (getDigitalProducts proxy).
    return (
      mf.course_materials?.section_data_mapping?.value ??
      mf.course_materials?.section_data_mapping ??
      mf['course_materials.section_data_mapping'] ??
      mf.course_materials_section_data_mapping ??
      mf.section_data_mapping?.value ??
      mf.section_data_mapping ??
      null
    );
  }

  /**
   * @param {unknown} product
   * @returns {{ id: string | null, sectionDataMapping: unknown, ddcsSectionEntries: Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>, isAdoptedTitle: boolean }}
   */
  function normalizeAccessProductRecord(product) {
    if (!product || typeof product !== 'object') {
      return {
        id: null,
        sectionDataMapping: [],
        ddcsSectionEntries: [],
        isAdoptedTitle: false,
      };
    }

    let id = product.id ?? null;
    if (id != null) id = String(id).split('/').pop();

    const fromProduct = extractDdcsSectionEntriesFromProduct(product);
    const fromConfig =
      Array.isArray(product.ddcsSectionEntries) || Array.isArray(product.ddcs_section_entries)
        ? normalizeDdcsSectionEntries(
            product.ddcsSectionEntries ?? product.ddcs_section_entries,
          )
        : [];

    return {
      id,
      sectionDataMapping: normalizeSectionDataMapping(
        extractSectionDataMappingFromProduct(product),
      ),
      ddcsSectionEntries: fromProduct.length > 0 ? fromProduct : fromConfig,
      isAdoptedTitle: productHasAdoptedTitleTag(product),
    };
  }

  /**
   * @param {unknown} raw
   * @returns {Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>}
   */
  function normalizeDdcsSectionEntries(raw) {
    let arr = raw;
    if (arr == null) return [];
    if (typeof arr === 'string') {
      try {
        arr = JSON.parse(arr);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(arr)) return [];
    /** @type {Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>} */
    const out = [];
    arr.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const sectionId = String(
        entry.section_id ??
          entry.sectionId ??
          entry.SectionID ??
          entry.section_guid ??
          entry.gid ??
          '',
      ).trim();
      const termRaw = entry.term_id ?? entry.termId ?? entry.term_id_guid ?? null;
      const termId =
        termRaw != null && String(termRaw).trim() !== '' ? String(termRaw).trim() : null;
      const disabledRaw = entry.disabled_flag ?? entry.disabledFlag ?? false;
      const disabled_flag =
        disabledRaw === true || String(disabledRaw).toLowerCase() === 'true';
      if (!sectionId && !termId) return;
      out.push({ section_id: sectionId, term_id: termId, disabled_flag });
    });
    return out;
  }

  /**
   * @param {unknown} product
   * @returns {Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>}
   */
  function extractDdcsSectionEntriesFromProduct(product) {
    if (!product || typeof product !== 'object') return [];
    const direct =
      product.ddcsSectionEntries ??
      product.ddcs_section_entries ??
      null;
    if (direct != null) return normalizeDdcsSectionEntries(direct);

    const mf = product.metafields;
    if (!mf) return [];
    if (Array.isArray(mf)) {
      return normalizeDdcsSectionEntries(getMetafieldFromArray(mf, 'ddcs', 'ddcs_section'));
    }
    if (typeof mf !== 'object') return [];
    return normalizeDdcsSectionEntries(
      mf.ddcs?.ddcs_section?.value ??
        mf.ddcs?.ddcs_section ??
        mf['ddcs.ddcs_section'] ??
        mf.ddcs_section?.value ??
        mf.ddcs_section ??
        null,
    );
  }

  /**
   * @param {unknown} tagsOrProduct
   * @returns {boolean}
   */
  function productHasAdoptedTitleTag(tagsOrProduct) {
    if (tagsOrProduct === true) return true;
    if (tagsOrProduct === false) return false;
    if (tagsOrProduct && typeof tagsOrProduct === 'object') {
      if ('isAdoptedTitle' in tagsOrProduct) {
        return tagsOrProduct.isAdoptedTitle === true;
      }
    }
    const tags =
      tagsOrProduct && typeof tagsOrProduct === 'object'
        ? tagsOrProduct.tags
        : tagsOrProduct;
    if (tags == null) return false;
    if (typeof tags === 'string') {
      return tags.split(/[\s,]+/).some((t) => String(t).toLowerCase() === 'adoptedtitle');
    }
    if (Array.isArray(tags)) {
      return tags.some((t) => String(t).toLowerCase() === 'adoptedtitle');
    }
    return false;
  }

  /**
   * @returns {Map<string, { id: string | null, sectionDataMapping: unknown, ddcsSectionEntries: Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>, isAdoptedTitle: boolean }>}
   */
  function buildProductAccessRegistry() {
    /** @type {Map<string, { id: string | null, sectionDataMapping: unknown, ddcsSectionEntries: Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>, isAdoptedTitle: boolean }>} */
    const registry = new Map();
    const el = document.getElementById('pdp-follett-access-config');
    if (el) {
      try {
        const parsed = JSON.parse(el.textContent || '{}');
        const rootId = parsed.productId != null ? String(parsed.productId) : null;
        const root = {
          id: rootId,
          sectionDataMapping: normalizeSectionDataMapping(parsed.sectionDataMapping),
          ddcsSectionEntries: normalizeDdcsSectionEntries(parsed.ddcsSectionEntries),
          isAdoptedTitle: parsed.isAdoptedTitle === true,
        };
        if (rootId) registry.set(rootId, root);
      } catch {
        /* ignore */
      }
    }
    const addons = Array.isArray(window.__pdpFollettAccessAddonProducts)
      ? window.__pdpFollettAccessAddonProducts
      : [];
    addons.forEach((product) => {
      if (product?.id != null) {
        registry.set(String(product.id), product);
      }
    });
    return registry;
  }

  /**
   * Merge unified sibling products into the addon registry (by product id).
   * @param {unknown[]} products
   */
  function registerAddonProducts(products) {
    const incoming = (Array.isArray(products) ? products : []).map(normalizeAccessProductRecord);
    const existing = Array.isArray(window.__pdpFollettAccessAddonProducts)
      ? window.__pdpFollettAccessAddonProducts
      : [];
    /** @type {Map<string, ReturnType<typeof normalizeAccessProductRecord>>} */
    const byId = new Map();
    existing.forEach((record) => {
      if (record?.id != null) byId.set(String(record.id), record);
    });
    incoming.forEach((record) => {
      if (record?.id != null) byId.set(String(record.id), record);
    });
    window.__pdpFollettAccessAddonProducts = Array.from(byId.values());
  }

  /**
   * Root section_data_mapping plus all unified sibling mappings (for program-name lookup).
   * @param {unknown} rootSectionDataMapping
   * @returns {unknown[]}
   */
  function mergeSectionDataMappingsForProgramLookup(rootSectionDataMapping) {
    /** @type {unknown[]} */
    const merged = [];
    const root = normalizeSectionDataMapping(rootSectionDataMapping);
    if (Array.isArray(root)) merged.push(...root);
    const addons = Array.isArray(window.__pdpFollettAccessAddonProducts)
      ? window.__pdpFollettAccessAddonProducts
      : [];
    addons.forEach((product) => {
      const mapping = normalizeSectionDataMapping(product?.sectionDataMapping);
      if (Array.isArray(mapping)) merged.push(...mapping);
    });
    return merged;
  }

  /**
   * Root + unified sibling ddcs.ddcs_section entries (section_id → term_id).
   * @param {unknown} rootEntries
   * @returns {Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>}
   */
  function mergeDdcsSectionEntriesForProgramLookup(rootEntries) {
    /** @type {Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>} */
    const merged = [...normalizeDdcsSectionEntries(rootEntries)];
    const addons = Array.isArray(window.__pdpFollettAccessAddonProducts)
      ? window.__pdpFollettAccessAddonProducts
      : [];
    addons.forEach((product) => {
      const entries = normalizeDdcsSectionEntries(product?.ddcsSectionEntries);
      if (entries.length) merged.push(...entries);
    });
    return merged;
  }

  /**
   * @param {unknown} mapping
   * @returns {string[]}
   */
  function collectSectionIdsFromMapping(mapping) {
    let arr = mapping;
    if (typeof arr === 'string') {
      try {
        arr = JSON.parse(arr);
      } catch {
        arr = [];
      }
    }
    if (!Array.isArray(arr)) return [];
    return [
      ...new Set(
        arr
          .map((item) => item?.SectionID ?? item?.section_id ?? item?.sectionId)
          .filter((id) => id != null && String(id).trim() !== '')
          .map((id) => String(id).trim()),
      ),
    ];
  }

  /**
   * @param {string} a
   * @param {string} b
   * @returns {boolean}
   */
  function sameDdcsId(a, b) {
    return String(a ?? '')
      .trim()
      .toLowerCase() ===
      String(b ?? '')
        .trim()
        .toLowerCase();
  }

  /**
   * @param {unknown} flag
   * @returns {boolean}
   */
  function isTruthyFlag(flag) {
    return flag === true || String(flag).toLowerCase() === 'true';
  }

  /**
   * Active term: not disabled; when is_term_open_flag is present, it must be true.
   * @param {any} term
   * @returns {boolean}
   */
  function isActiveTermRow(term) {
    if (!term || typeof term !== 'object') return false;
    if (isTruthyFlag(term.disabled_flag ?? term.disabledFlag)) return false;
    const open = term.is_term_open_flag ?? term.isTermOpenFlag;
    if (open !== undefined && open !== null && String(open).trim() !== '') {
      return isTruthyFlag(open);
    }
    return true;
  }

  /**
   * @param {any[]} terms
   * @returns {any | null}
   */
  function pickFurthestRentalEndTerm(terms) {
    return terms
      .filter((t) => t?.rental_end_date)
      .reduce((latest, current) => {
        if (!latest) return current;
        return new Date(current.rental_end_date) > new Date(latest.rental_end_date)
          ? current
          : latest;
      }, null);
  }

  /**
   * @param {string[]} sectionIds
   * @param {Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>} entries
   * @returns {{ termIds: string[]; missingSectionIds: string[] }}
   */
  function collectTermIdsFromSectionEntries(sectionIds, entries) {
    const activeEntries = entries.filter((e) => !e.disabled_flag);
    /** @type {Set<string>} */
    const termIds = new Set();
    /** @type {Set<string>} */
    const coveredSections = new Set();

    const restrictToMapping = sectionIds.length > 0;
    activeEntries.forEach((entry) => {
      if (restrictToMapping) {
        const match = sectionIds.some((sid) => sameDdcsId(sid, entry.section_id));
        if (!match) return;
      }
      if (entry.term_id) {
        termIds.add(entry.term_id);
        if (entry.section_id) coveredSections.add(entry.section_id.toLowerCase());
      }
    });

    const missingSectionIds = restrictToMapping
      ? sectionIds.filter((sid) => !coveredSections.has(sid.toLowerCase()))
      : [];

    return { termIds: [...termIds], missingSectionIds };
  }

  /**
   * @param {string[]} termIds
   * @param {any[]} termRows
   * @returns {any[]}
   */
  function filterTermsByIds(termIds, termRows) {
    if (!Array.isArray(termRows) || termRows.length === 0 || termIds.length === 0) {
      return [];
    }
    return termRows.filter((t) => {
      if (!isActiveTermRow(t)) return false;
      const tid = t?.term_id ?? t?.term;
      return termIds.some((id) => sameDdcsId(id, tid));
    });
  }

  /**
   * @param {string} campusEntityId
   * @param {any[]} campuses
   * @returns {any | null}
   */
  function findIncludedCampus(campusEntityId, campuses) {
    if (!campusEntityId || !Array.isArray(campuses)) return null;
    const matches = campuses.filter((c) =>
      sameDdcsId(c?.campus_entity_id ?? c?.campusEntityId, campusEntityId),
    );
    return (
      matches.find((c) =>
        isTruthyFlag(c?.included_enabled_flag ?? c?.includedEnabledFlag),
      ) ?? null
    );
  }

  /**
   * Page is Access (root or unified sibling IncludEDMaterialFlag).
   * @param {unknown} [sectionDataMapping]
   * @returns {boolean}
   */
  function isAccessPage(sectionDataMapping) {
    const pageAccess = readConfigSync().includedMaterial;
    return pageAccess || productHasIncludedMaterialFlag(sectionDataMapping);
  }

  /**
   * Access + SKU in noPurchase_AccessSKUs. No format or sales-channel check.
   * No-op when the shop metafield is missing/empty.
   * @param {unknown} variant
   * @param {unknown} [sectionDataMapping]
   * @returns {boolean}
   */
  function shouldHideAccessBlockedSku(variant, sectionDataMapping) {
    if (!isAccessPage(sectionDataMapping)) return false;
    return isNoPurchaseAccessSku(variant?.sku);
  }

  /**
   * Hide digital variants that lack SC, or any Access variant whose SKU is in
   * noPurchase_AccessSKUs, when the PDP is Access.
   * Page-level Access (root OR any unified sibling with IncludEDMaterialFlag) applies
   * to all digital variants. The passed mapping is a fallback before addons register.
   * @param {unknown} sectionDataMapping
   * @param {unknown} variant
   * @returns {boolean}
   */
  function shouldHideDigitalVariantForMapping(sectionDataMapping, variant) {
    if (shouldHideAccessBlockedSku(variant, sectionDataMapping)) return true;
    if (!isAccessPage(sectionDataMapping)) return false;
    if (!isDigitalVariant(variant, false)) return false;
    const raw = getVariantSalesChannelRaw(variant);
    if (raw === '') return true;
    return !salesChannelTokensContainSc(raw);
  }

  /**
   * @param {boolean} includedMaterial
   * @param {unknown} variant
   * @returns {boolean}
   */
  function shouldHideDigitalVariant(includedMaterial, variant) {
    if (!includedMaterial) return false;
    return shouldHideDigitalVariantForMapping(
      [{ IncludEDMaterialFlag: true }],
      variant,
    );
  }

  /**
   * @returns {{ isAdoptedTitle: boolean; includedMaterial: boolean; sectionDataMapping: unknown; ddcsSectionEntries: Array<{ section_id: string, term_id: string | null, disabled_flag: boolean }>; productId: string | null }}
   */
  function readConfigSync() {
    const el = document.getElementById('pdp-follett-access-config');
    /** @type {{ isAdoptedTitle?: boolean; sectionDataMapping?: unknown; ddcsSectionEntries?: unknown; productId?: string | number }} */
    let parsed = {};
    if (el) {
      try {
        parsed = JSON.parse(el.textContent || '{}');
      } catch {
        parsed = {};
      }
    }
    const isAdoptedTitle = parsed.isAdoptedTitle === true;
    const sectionDataMapping = normalizeSectionDataMapping(parsed.sectionDataMapping);
    const ddcsSectionEntries = normalizeDdcsSectionEntries(parsed.ddcsSectionEntries);
    const productId = parsed.productId != null ? String(parsed.productId) : null;
    const rootIncluded = productHasIncludedMaterialFlag(sectionDataMapping);
    const addonProducts = Array.isArray(window.__pdpFollettAccessAddonProducts)
      ? window.__pdpFollettAccessAddonProducts
      : [];
    const addonIncluded = addonProducts.some((p) =>
      productHasIncludedMaterialFlag(p.sectionDataMapping),
    );
    const includedMaterial = isAdoptedTitle && (rootIncluded || addonIncluded);
    return {
      isAdoptedTitle,
      includedMaterial,
      sectionDataMapping,
      ddcsSectionEntries,
      productId,
    };
  }

  /**
   * Chunked section → term_id metaobjects lookup (fallback when Liquid/API entries lack term_id).
   * @param {string[]} sectionIds
   * @param {(type: string, query: string) => Promise<any[]>} fetchMeta
   * @returns {Promise<string[]>}
   */
  async function fetchTermIdsForSectionsChunked(sectionIds, fetchMeta) {
    if (!sectionIds.length) return [];
    const CHUNK = 20;
    /** @type {string[]} */
    const termIds = [];
    for (let i = 0; i < sectionIds.length; i += CHUNK) {
      const chunk = sectionIds.slice(i, i + CHUNK);
      const sectionOrQuery = chunk
        .map((id) => `(fields.section:'${id}' OR fields.section_id:'${id}')`)
        .join(' OR ');
      const sections = await fetchMeta(
        'ddcs_section',
        `(${sectionOrQuery}) fields.disabled_flag:false`,
      );
      sections.forEach((s) => {
        const tid = s?.term_id;
        if (tid != null && String(tid).trim() !== '') termIds.push(String(tid).trim());
      });
    }
    return [...new Set(termIds)];
  }

  /**
   * @param {string} campusEntityId
   * @param {(type: string, query: string) => Promise<any[]>} fetchMeta
   * @returns {Promise<string|null>}
   */
  async function resolveProgramNameFromCampusEntityId(campusEntityId, fetchMeta) {
    if (!campusEntityId) return null;

    const cachedCampuses = Array.isArray(window.__pdpDdcsCampuses)
      ? window.__pdpDdcsCampuses
      : [];
    let includedCampus = findIncludedCampus(campusEntityId, cachedCampuses);

    if (!includedCampus) {
      for (const campusType of ['ddcs_campus_entity', 'ddcs_campus']) {
        const campuses = await fetchMeta(
          campusType,
          `fields.campus_entity_id:'${campusEntityId}' fields.disabled_flag:false`,
        );
        includedCampus = findIncludedCampus(campusEntityId, campuses);
        if (includedCampus) break;
      }
    }

    if (!includedCampus) return null;
    const name = formatAccessProgramName(
      includedCampus?.included_program_name ?? includedCampus?.includedProgramName,
    );
    if (name) return name;
    return getAccessProgramSetting('cmrpAccessProgramDefaultName') || DEFAULT_PROGRAM_NAME;
  }

  /**
   * Resolve program label: product adoption sections → active terms → furthest
   * rental_end_date → campus included_program_name.
   * Prefers client-side join (cached open terms + product term_ids) to avoid mega section URLs.
   * @param {unknown} sectionDataMapping
   * @param {unknown} [ddcsSectionEntries]
   * @returns {Promise<string|null>}
   */
  async function resolveAccessProgramLabelFromLongestTerm(
    sectionDataMapping,
    ddcsSectionEntries,
  ) {
    const apiUrl = '/apps/shopifyData/metaobjects';
    const sectionIds = collectSectionIdsFromMapping(sectionDataMapping);
    const entries = mergeDdcsSectionEntriesForProgramLookup(ddcsSectionEntries);
    if (sectionIds.length === 0 && entries.length === 0) return null;

    const fetchMeta = async (type, query) => {
      const url = `${apiUrl}?type=${type}&query=${encodeURIComponent(query)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return [];
      const body = await res.json();
      return Array.isArray(body?.data) ? body.data : [];
    };

    let { termIds, missingSectionIds } = collectTermIdsFromSectionEntries(
      sectionIds,
      entries,
    );

    if (missingSectionIds.length > 0) {
      const fromSections = await fetchTermIdsForSectionsChunked(
        missingSectionIds,
        fetchMeta,
      );
      termIds = [...new Set([...termIds, ...fromSections])];
    } else if (termIds.length === 0 && sectionIds.length > 0) {
      termIds = await fetchTermIdsForSectionsChunked(sectionIds, fetchMeta);
    }

    /** @type {any[]} */
    let openTerms = Array.isArray(window.__pdpDdcsOpenTerms)
      ? window.__pdpDdcsOpenTerms
      : [];
    if (openTerms.length === 0 && typeof window.fetchFilteredCampusTermLists === 'function') {
      // Open-term cache may still be empty if metaobjects open-term fetch failed;
      // filtered lists are a secondary source (may be slim / missing dates).
      try {
        const lists =
          Array.isArray(window.__pdpDdcsTerms) && window.__pdpDdcsTerms.length > 0
            ? { campuses: window.__pdpDdcsCampuses, terms: window.__pdpDdcsTerms }
            : await window.fetchFilteredCampusTermLists();
        if (Array.isArray(lists?.terms)) openTerms = lists.terms;
        if (Array.isArray(lists?.campuses) && !Array.isArray(window.__pdpDdcsCampuses)) {
          window.__pdpDdcsCampuses = lists.campuses;
        }
      } catch {
        /* ignore */
      }
    }

    let terms = filterTermsByIds(termIds, openTerms);

    // Targeted term fetch if product term_ids are known but not in cache (or lack dates).
    if (
      (terms.length === 0 || !terms.some((t) => t?.rental_end_date)) &&
      termIds.length > 0
    ) {
      const CHUNK = 20;
      /** @type {any[]} */
      const fetched = [];
      for (let i = 0; i < termIds.length; i += CHUNK) {
        const chunk = termIds.slice(i, i + CHUNK);
        const termOrQuery = chunk
          .map((id) => `(fields.term:'${id}' OR fields.term_id:'${id}')`)
          .join(' OR ');
        const rows = await fetchMeta(
          'ddcs_term',
          `(${termOrQuery}) fields.disabled_flag:false`,
        );
        fetched.push(...rows.filter(isActiveTermRow));
      }
      if (fetched.length > 0) terms = fetched;
    }

    if (terms.length === 0) return null;

    const longestTerm = pickFurthestRentalEndTerm(terms);
    const campusEntityId = longestTerm?.campus_entity_id;
    if (!campusEntityId) return null;

    return resolveProgramNameFromCampusEntityId(campusEntityId, fetchMeta);
  }

  /**
   * @returns {Promise<{ isAdoptedTitle: boolean; includedMaterial: boolean; programName: string }>}
   */
  async function readConfig() {
    const sync = readConfigSync();
    const unifiedSectionDataMapping = mergeSectionDataMappingsForProgramLookup(
      sync.sectionDataMapping,
    );
    const unifiedDdcsSectionEntries = mergeDdcsSectionEntriesForProgramLookup(
      sync.ddcsSectionEntries,
    );

    let programName = getAccessProgramSetting('cmrpAccessProgramDefaultName') || DEFAULT_PROGRAM_NAME;
    const fromTerm = await resolveAccessProgramLabelFromLongestTerm(
      unifiedSectionDataMapping,
      unifiedDdcsSectionEntries,
    );
    if (fromTerm) {
      programName = fromTerm;
    } else {
      const campus = window.__pdpDdcsCampusForRental;
      if (campus && typeof campus === 'object') {
        const name = formatAccessProgramName(
          campus.included_program_name ??
            campus.includedProgramName ??
            campus.included_section_note,
        );
        if (name) {
          programName = name;
        }
      }
    }

    return { ...sync, programName };
  }

  /**
   * @param {string} programName
   * @returns {string}
   */
  function accessBannerHtml(programName) {
    const label = resolveAccessProgramLabel(programName);
    const bannerText = fillAccessProgramPlaceholderForHtml(
      getAccessProgramSetting('cmrpAccessProgramMaterialBannerText'),
      label
    );
    return (
      `<div class="rental-banner access-program-style pdp-follett-access-banner" data-pdp-access-banner>` +
      `<span class="access-program-text-product">${bannerText}</span>` +
      `</div>`
    );
  }

  /**
   * @param {string} programName
   * @returns {string}
   */
  function accessTagHtml(programName) {
    const label = resolveAccessProgramLabel(programName);
    const badgeText = fillAccessProgramPlaceholderForHtml(
      getAccessProgramSetting('cmrpAccessProgramBannerText'),
      label
    );
    const tooltipTitle = fillAccessProgramPlaceholderForHtml(
      getAccessProgramSetting('cmrpAccessProgramTitleTemplate'),
      label,
      true
    );
    const tooltipBody = fillAccessProgramPlaceholderForHtml(
      getAccessProgramSetting('cmrpAccessProgramBodyText'),
      label
    );
    return (
      `<div class="rental-banner access-program-style pdp-follett-access-tag" data-pdp-access-tag>` +
      `<span class="tooltip-trigger access-program-tooltip" tabindex="0">` +
      `<span class="access-program-text">${badgeText}</span>` +
      `<span class="access-program-tooltip-content" role="tooltip" aria-hidden="true">` +
      `<span class="access-program-tooltip-header">` +
      `<span class="access-program-tooltip-title">${tooltipTitle}</span>` +
      `</span>` +
      `<span class="access-program-tooltip-body">${tooltipBody}</span>` +
      `</span>` +
      `</span>` +
      `</div>`
    );
  }

  /**
   * @param {Element} row
   */
  function unhideDigitalRow(row) {
    row.removeAttribute('data-fa-digital-blocked');
    row.removeAttribute('data-fa-sales-channel');
    if (row instanceof HTMLElement) {
      row.hidden = false;
      row.style.display = '';
    }
  }

  /**
   * Page-level Access context for a DOM row. Once any root/unified product has
   * IncludEDMaterialFlag, includedMaterial is true for every digital row.
   * @param {Element} row
   * @param {Map<string, { sectionDataMapping: unknown }>} registry
   * @returns {{ productId: string | null; includedMaterial: boolean; sectionDataMapping: unknown }}
   */
  function resolveRowAccessContext(row, registry) {
    const productId = row.getAttribute('data-fa-product-id');
    const sync = readConfigSync();
    const record =
      productId && registry.has(productId) ? registry.get(productId) : null;
    const mapping = record?.sectionDataMapping ?? sync.sectionDataMapping;
    return {
      productId: productId || sync.productId,
      includedMaterial: sync.includedMaterial,
      sectionDataMapping: mapping,
    };
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {boolean}
   */
  function hasVisiblePrintVariants(wrapper) {
    return Array.from(
      wrapper.querySelectorAll('.accordion-variant-option, .purchase-type'),
    ).some((row) => {
      if (!(row instanceof HTMLElement)) return false;
      if (rowIsDigital(row)) return false;
      if (row.getAttribute('data-fa-digital-blocked') === 'true') return false;
      if (row.hidden || row.style.display === 'none') return false;
      const panel = row.closest('.format-tab-panel');
      if (panel instanceof HTMLElement && (panel.hidden || panel.style.display === 'none')) {
        return false;
      }
      return true;
    });
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {number}
   */
  function countVisiblePrintRows(wrapper) {
    let n = 0;
    wrapper.querySelectorAll('.accordion-variant-option, .purchase-type').forEach((row) => {
      if (rowIsDigital(row)) return;
      if (row.getAttribute('data-fa-digital-blocked') === 'true') return;
      if (row instanceof HTMLElement && (row.hidden || row.style.display === 'none')) return;
      n += 1;
    });
    return n;
  }

  /**
   * Show print tab when print options remain; hide it on Access when blocked SKUs
   * removed every print row.
   * @param {HTMLElement} wrapper
   */
  function ensurePrintTabVisible(wrapper) {
    const printTab = wrapper.querySelector(
      '[data-testid="format-tab-print"], [data-format="print"].format-tab',
    );
    const printPanel = wrapper.querySelector(
      '.format-tab-panel[data-format="print"], #format-panel-print',
    );
    if (countVisiblePrintRows(wrapper) > 0) {
      if (printTab instanceof HTMLElement) {
        printTab.style.display = '';
        printTab.removeAttribute('aria-hidden');
      }
      if (printPanel instanceof HTMLElement) {
        printPanel.hidden = false;
        printPanel.style.display = '';
      }
      return;
    }
    if (!readConfigSync().includedMaterial) return;
    if (printTab instanceof HTMLElement) hideDigitalTab(printTab);
    if (printPanel instanceof HTMLElement) hideDigitalPanel(printPanel);
    activateFirstVisibleFormatTab(wrapper);
  }

  /**
   * @param {HTMLElement} tab
   */
  function showDigitalTab(tab) {
    tab.style.display = '';
    tab.removeAttribute('aria-hidden');
  }

  /**
   * @param {HTMLElement} panel
   */
  function showDigitalPanel(panel) {
    panel.hidden = false;
    // Un-hide for tab switching, but don't override the focused format tab.
    if (panel.classList.contains('active')) {
      panel.style.display = 'block';
    } else {
      panel.style.display = 'none';
    }
  }

  /**
   * @param {Element} row
   * @returns {boolean}
   */
  function rowIsDigital(row) {
    const cb = row.querySelector('.purchase-checkbox');
    if (cb) {
      return String(cb.getAttribute('data-is-digital') || '').toLowerCase() === 'true';
    }
    const fmt = String(row.getAttribute('data-format-specific') || '').toLowerCase();
    return fmt === 'digital';
  }

  /**
   * @param {Element} row
   * @param {string} salesChannelRaw
   */
  function annotateDigitalRow(row, salesChannelRaw) {
    row.setAttribute('data-fa-sales-channel', salesChannelRaw);
    row.setAttribute('data-fa-digital-blocked', 'true');
    row.style.display = 'none';
    row.hidden = true;
  }

  /**
   * @param {Element} row
   */
  function hideDigitalPurchaseRow(row) {
    const checkbox = row.querySelector('.purchase-checkbox');
    if (checkbox) {
      checkbox.checked = false;
      checkbox.disabled = true;
      checkbox.removeAttribute('name');
    }
    const checkboxContainer = row.querySelector('.purchase-checkbox-container');
    if (checkboxContainer) checkboxContainer.remove();

    row.querySelectorAll('.accordion-variant-price, .purchase-price').forEach((el) => {
      el.remove();
    });
  }

  /**
   * @param {HTMLElement} digitalTab
   */
  function hideDigitalTab(digitalTab) {
    digitalTab.style.display = 'none';
    digitalTab.setAttribute('aria-hidden', 'true');
    digitalTab.classList.remove('active');
    digitalTab.setAttribute('aria-selected', 'false');
  }

  /**
   * @param {HTMLElement} digitalPanel
   */
  function hideDigitalPanel(digitalPanel) {
    digitalPanel.hidden = true;
    digitalPanel.style.display = 'none';
    digitalPanel.classList.remove('active');
  }

  /**
   * @param {Element} row
   * @param {Map<string, { sectionDataMapping: unknown }>} [registry]
   * @returns {boolean}
   */
  /**
   * @param {Element} row
   * @returns {string}
   */
  function getRowSku(row) {
    const cb = row.querySelector('.purchase-checkbox');
    return (
      cb?.getAttribute('data-sku') ||
      row.getAttribute('data-sku') ||
      ''
    );
  }

  function shouldBlockDigitalDomRow(row, registry) {
    const reg = registry ?? buildProductAccessRegistry();
    const ctx = resolveRowAccessContext(row, reg);
    if (!ctx.includedMaterial) return false;
    if (isNoPurchaseAccessSku(getRowSku(row))) return true;
    if (!rowIsDigital(row)) return false;
    const cb = row.querySelector('.purchase-checkbox');
    let salesChannelRaw = cb?.getAttribute('data-sales-channel') || '';
    if (!salesChannelRaw && cb) {
      salesChannelRaw = cb.getAttribute('data-fa-sales-channel') || '';
    }
    if (salesChannelRaw === '') return true;
    return !salesChannelTokensContainSc(salesChannelRaw);
  }

  /**
   * Hide (or restore) digital DOM rows when the PDP is Access (any product flagged).
   * @param {HTMLElement} wrapper
   */
  function applyDigitalRowAccessRules(wrapper) {
    const registry = buildProductAccessRegistry();
    wrapper.querySelectorAll('.accordion-variant-option, .purchase-type').forEach((row) => {
      if (shouldBlockDigitalDomRow(row, registry)) {
        const cb = row.querySelector('.purchase-checkbox');
        const sc =
          cb?.getAttribute('data-sales-channel') ||
          cb?.getAttribute('data-fa-sales-channel') ||
          '';
        annotateDigitalRow(row, sc);
        hideDigitalPurchaseRow(row);
      } else if (row.getAttribute('data-fa-digital-blocked') === 'true') {
        unhideDigitalRow(row);
      }
    });
  }

  /**
   * @param {HTMLElement} wrapper
   * @returns {{ digitalRowsTotal: number; digitalRowsVisible: number; digitalTabHidden: boolean }}
   */
  function updateDigitalTabVisibility(wrapper) {
    let digitalRowsTotal = 0;
    let digitalRowsVisible = 0;

    wrapper.querySelectorAll('.accordion-variant-option, .purchase-type').forEach((row) => {
      if (!rowIsDigital(row)) return;
      digitalRowsTotal += 1;
      if (row.getAttribute('data-fa-digital-blocked') === 'true') return;
      if (row instanceof HTMLElement && (row.hidden || row.style.display === 'none')) return;
      digitalRowsVisible += 1;
    });

    const digitalTab = wrapper.querySelector(
      '[data-testid="format-tab-digital"], [data-format="digital"].format-tab',
    );
    const digitalPanel = wrapper.querySelector(
      '.format-tab-panel[data-format="digital"], #format-panel-digital',
    );

    let digitalTabHidden = false;
    if (digitalTab instanceof HTMLElement) {
      if (digitalRowsVisible === 0) {
        digitalTabHidden = true;
        hideDigitalTab(digitalTab);
        if (digitalPanel instanceof HTMLElement) hideDigitalPanel(digitalPanel);
        if (!hasVisiblePrintVariants(wrapper)) {
          activateFirstVisibleFormatTab(wrapper);
        }
      } else {
        showDigitalTab(digitalTab);
        if (digitalPanel instanceof HTMLElement) showDigitalPanel(digitalPanel);
      }
    }

    return { digitalRowsTotal, digitalRowsVisible, digitalTabHidden };
  }

  /**
   * Reveal format tabs after unified PDP access filtering and render complete.
   */
  function revealPendingFormatTabs() {
    document.querySelectorAll('[data-fa-pdp-pending]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.removeAttribute('data-fa-pdp-pending');
      el.style.visibility = '';
    });
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function activateFirstVisibleFormatTab(wrapper) {
    const tabs = Array.from(wrapper.querySelectorAll('.format-tab')).filter(
      (t) => t.style.display !== 'none',
    );
    const panels = wrapper.querySelectorAll('.format-tab-panel');
    tabs.forEach((t, i) => {
      const active = i === 0;
      t.classList.toggle('active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    panels.forEach((p) => {
      const format = p.getAttribute('data-format');
      const tab = tabs.find((t) => t.getAttribute('data-format') === format);
      const show = !!tab;
      p.classList.toggle('active', show);
      if (show) {
        p.hidden = false;
        p.style.display = 'block';
      } else {
        hideDigitalPanel(/** @type {HTMLElement} */ (p));
      }
    });
  }

  /**
   * @param {boolean} includedMaterial
   * @param {string} programName
   */
  function ensureAccessMessaging(includedMaterial, programName) {
    if (!includedMaterial) return;

    const bannerHtml = accessBannerHtml(programName);
    const picker = document.querySelector('.textbook-variant-picker');
    if (picker) {
      const existingBanner = picker.querySelector('[data-pdp-access-banner]');
      if (existingBanner) {
        existingBanner.outerHTML = bannerHtml;
      } else {
        const titleBlock = picker.querySelector('.variant_picker_title');
        if (titleBlock) {
          titleBlock.insertAdjacentHTML('beforebegin', bannerHtml);
        }
      }
    }

    const tagHtml = accessTagHtml(programName);
    const priceEl = document.querySelector(
      '.product-details product-price, product-price[data-product-id]',
    );
    const inlineTargets = document.querySelectorAll('[data-access-program-inline]');
    if (inlineTargets.length > 0) {
      inlineTargets.forEach((target) => {
        target.innerHTML = tagHtml;
      });
    } else if (priceEl) {
      const existingTag = priceEl.querySelector('[data-pdp-access-tag]');
      if (existingTag) {
        existingTag.outerHTML = tagHtml;
      } else {
        priceEl.insertAdjacentHTML('beforeend', tagHtml);
      }
    }
  }

  /**
   * @param {HTMLElement | null} wrapper
   * @returns {boolean}
   */
  function hasPurchasableVariants(wrapper) {
    if (!wrapper) return false;

    const rows = wrapper.querySelectorAll('.accordion-variant-option, .purchase-type');
    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;
      if (row.getAttribute('data-fa-digital-blocked') === 'true') continue;

      const panel = row.closest('.format-tab-panel');
      if (panel instanceof HTMLElement) {
        if (panel.hidden || panel.style.display === 'none') continue;
      }

      const cb = row.querySelector('.purchase-checkbox');
      if (cb instanceof HTMLInputElement && !cb.disabled) return true;
    }

    return false;
  }

  /**
   * @param {boolean} visible
   */
  function setPurchaseUiVisible(visible) {
    document
      .querySelectorAll(
        '.textbook-variant-picker .format-tabs-wrapper, .format-tabs-wrapper',
      )
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.hidden = !visible;
        el.style.display = visible ? '' : 'none';
        if (visible) el.removeAttribute('data-fa-purchase-hidden');
        else el.setAttribute('data-fa-purchase-hidden', 'true');
      });

    document
      .querySelectorAll('.textbook-variant-picker .variant_picker_title')
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.hidden = !visible;
        el.style.display = visible ? '' : 'none';
        if (visible) el.removeAttribute('data-fa-purchase-hidden');
        else el.setAttribute('data-fa-purchase-hidden', 'true');
      });

    document.querySelectorAll('.buy-buttons-block .product-form-buttons').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.hidden = !visible;
      el.style.display = visible ? '' : 'none';
      if (visible) el.classList.remove('pdp-follett-access-no-purchase');
      else el.classList.add('pdp-follett-access-no-purchase');
    });
  }

  /**
   * Print format tab still offered on this picker (not Access-hidden).
   * @param {HTMLElement} wrapper
   * @returns {boolean}
   */
  function hasPrintFormatTab(wrapper) {
    const printTab = wrapper.querySelector(
      '[data-testid="format-tab-print"], [data-format="print"].format-tab',
    );
    if (!(printTab instanceof HTMLElement)) return false;
    if (printTab.style.display === 'none') return false;
    if (printTab.getAttribute('aria-hidden') === 'true') return false;
    return true;
  }

  /**
   * After Access digital hide rules:
   * - Print tab / print variants present → keep tabs + ATB visible (print stays purchasable).
   * - No print, Access PDP, and nothing left to purchase → hide ATB (and tabs; banner replaces).
   * Covers JS-annotated blocked rows and SSR-omitted digitals (Liquid never rendered a row,
   * so `data-fa-digital-blocked` is absent — see `__variantTextbookPickerChecks`).
   * Does not disable ATB; only hides when Access left nothing to purchase.
   * @param {HTMLElement | null} wrapper
   */
  function updatePurchaseUiVisibility(wrapper) {
    if (!wrapper) return;

    if (hasVisiblePrintVariants(wrapper) || hasPrintFormatTab(wrapper)) {
      setPurchaseUiVisible(true);
      return;
    }

    const { includedMaterial } = readConfigSync();
    // Previously required `data-fa-digital-blocked` rows. SSR Access hide omits those rows
    // entirely, which left an empty format shell + ATB visible. Access + no purchasable
    // options is the correct signal when print is also absent.
    const accessLeftNothingToBuy =
      includedMaterial && !hasPurchasableVariants(wrapper);
    setPurchaseUiVisible(!accessLeftNothingToBuy);
  }

  /**
   * @returns {{ data: unknown | null; scriptPresent: boolean; parseError: boolean }}
   */

  /**
   * Apply Follett Access sales-channel rules to the current PDP DOM.
   * @returns {Promise<void>}
   */
  async function applyPdpRules() {
    try {
      await (window.__pdpDdcsRentalReadyPromise ?? Promise.resolve());
    } catch {
      /* ignore */
    }

    const syncConfig = readConfigSync();
    const { isAdoptedTitle, includedMaterial: includedMaterialSync } = syncConfig;

    if (!isAdoptedTitle) {
      return;
    }

    const wrapper =
      document.querySelector('.textbook-variant-picker .format-tabs-wrapper') ||
      document.querySelector('.format-tabs-wrapper');
    if (!wrapper) {
      return;
    }

    // Page-level Access + SC: hide non-SC digitals when any root/unified product is Access.
    applyDigitalRowAccessRules(/** @type {HTMLElement} */ (wrapper));
    updateDigitalTabVisibility(/** @type {HTMLElement} */ (wrapper));
    ensurePrintTabVisible(wrapper);

    const { programName } = await readConfig();

    if (includedMaterialSync) {
      ensureAccessMessaging(includedMaterialSync, programName);
    }
    updatePurchaseUiVisibility(wrapper);

    if (typeof window.syncPDPProductPriceRange === 'function') {
      window.syncPDPProductPriceRange();
    }
  }

  /**
   * @param {unknown} product
   * @param {boolean} [includedMaterial]
   * @returns {boolean}
   */
  function productHasDigitalVariant(product, includedMaterial = false) {
    const variants = product?.variants;
    if (!Array.isArray(variants)) return false;
    return variants.some((variant) => isDigitalVariant(variant, includedMaterial));
  }

  window.PdpFollettAccess = {
    salesChannelTokensContainSc,
    getVariantSalesChannelRaw,
    getNoPurchaseAccessSkuSet,
    isNoPurchaseAccessSku,
    isDigitalVariant,
    productHasAdoptedTitleTag,
    productHasIncludedMaterialFlag,
    productHasDigitalVariant,
    shouldHideDigitalVariantForMapping,
    shouldHideAccessBlockedSku,
    normalizeAccessProductRecord,
    registerAddonProducts,
    buildProductAccessRegistry,
    shouldHideDigitalVariant,
    shouldHideDigitalVariantForProduct(includedMaterial, variant) {
      return shouldHideDigitalVariant(includedMaterial, variant);
    },
    shouldBlockDigitalDomRow,
    updateDigitalTabVisibility,
    revealPendingFormatTabs,
    markPdpFollettAccessReady,
    waitForPdpFollettAccessReady,
    applyPdpRules,
    readConfig,
    readConfigSync,
  };
})();
